import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  Download,
  Key,
  FolderInput,
  LogIn,
  Terminal,
  ExternalLink,
  Zap,
  Mail,
  Play,
  Check
} from 'lucide-react';
import { Button } from '../components/Button';

export default function Guide() {
  const navigate = useNavigate();

  const handleDownload = async () => {
    try {
      const response = await fetch('/hollowpad.rbxmx');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'hollowpad.rbxmx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200 font-sans selection:bg-blue-500 selection:text-white overflow-x-hidden">

      {/* --- NAVBAR (Exact Replica) --- */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-950/80 backdrop-blur-md border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
            <img src="/logo512.png" alt="Logo" className="w-8 h-8 rounded-lg" />
            <span className="font-bold text-xl text-white tracking-tight">Hollowpad</span>
          </div>

          <div className="hidden md:flex items-center gap-8">
            <button onClick={() => navigate('/')} className="text-sm font-medium text-slate-400 hover:text-white transition-colors">Home</button>
            <button onClick={() => navigate('/#features')} className="text-sm font-medium text-slate-400 hover:text-white transition-colors">Features</button>
            <button onClick={() => navigate('/#pricing')} className="text-sm font-medium text-slate-400 hover:text-white transition-colors">Pricing</button>
          </div>

          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/auth')} className="text-sm font-medium text-white hover:text-blue-400 transition-colors hidden sm:block">
              Log in
            </button>
            <Button onClick={() => navigate('/console')} className="bg-blue-600 hover:bg-blue-500 text-white rounded-full px-6 py-2">
              Launch Console
            </Button>
          </div>
        </div>
      </nav>

      {/* --- HERO SECTION --- */}
      <section className="pt-32 pb-20 px-6 relative overflow-hidden">
        {/* BACKGROUND BLUR ORBS (The "Foreblur" effects) */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-blue-600/20 rounded-full blur-[120px] -z-10 pointer-events-none" />
        <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-indigo-600/10 rounded-full blur-[100px] -z-10 pointer-events-none" />
        <div className="absolute top-1/2 left-0 w-[300px] h-[300px] bg-blue-500/5 rounded-full blur-[80px] -z-10 pointer-events-none" />

        <div className="max-w-4xl mx-auto text-center space-y-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-900/30 border border-blue-500/30 text-blue-300 text-xs font-bold uppercase tracking-wider animate-in fade-in slide-in-from-bottom-4">
            <Zap size={12} className="fill-blue-400" /> Getting Started
          </div>

          <h1 className="text-5xl md:text-7xl font-extrabold text-white tracking-tight leading-tight animate-in fade-in slide-in-from-bottom-6 duration-700">
            Setup Hollowpad in <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-indigo-300">
               5 Simple Steps
            </span>
          </h1>

          <p className="text-lg md:text-xl text-slate-400 max-w-2xl mx-auto leading-relaxed animate-in fade-in slide-in-from-bottom-8 duration-1000">
            Follow this guide to link your Roblox Studio workspace with our AI agent and start building at 10x speed.
          </p>

          {/* Embedded YouTube Video with Gradient Fade Overlay */}
          <div className="mt-16 relative mx-auto max-w-5xl animate-in fade-in zoom-in duration-1000 delay-200">
            {/* The Glowing Background Effect */}
            <div className="absolute -inset-4 bg-blue-600/10 rounded-[2rem] blur-3xl -z-10" />

            <div className="rounded-xl border border-slate-800 bg-slate-900/80 p-1 shadow-2xl backdrop-blur-sm relative overflow-hidden">
              {/* Foreground Blur/Gradient Overlay (Matches landing preview style) */}
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-transparent to-transparent z-10 pointer-events-none" />
              
              <div className="relative w-full aspect-video overflow-hidden rounded-lg shadow-inner z-0">
                <iframe
                  className="absolute inset-0 w-full h-full"
                  src="https://www.youtube.com/embed/YOUR_VIDEO_ID_HERE?rel=0&modestbranding=1"
                  title="Hollowpad Demo"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                ></iframe>
              </div>
            </div>

            {/* Caption */}
            <div className="mt-6 flex items-center justify-center gap-2 text-slate-500 text-sm italic">
              <Play size={14} className="fill-slate-500" />
         <span>Get started with the Vibe-building with Hollowpad in a few minutes</span>
            </div>
          </div>
        </div>
      </section>

      {/* --- STEPS SECTION --- */}
      <section className="py-24 px-6 bg-slate-950 relative border-t border-slate-900">
        <div className="max-w-3xl mx-auto space-y-20">

          <Step
            number="1"
            title="Download the Plugin"
            icon={<Download className="text-blue-400" />}
            description="Download the Hollowpad Roblox Studio connector. This file allows the AI to communicate directly with your game editor."
          >
            <button
              onClick={handleDownload}
              className="mt-6 px-8 py-4 bg-blue-600 hover:bg-blue-500 text-white rounded-xl font-bold flex items-center gap-2 transition-all hover:scale-105 shadow-[0_0_40px_rgba(37,99,235,0.2)]"
            >
              <Download size={20} /> Download hollowpad.rbxmx
            </button>
          </Step>

          <Step
            number="2"
            title="Get Your Auth Key"
            icon={<Key className="text-blue-400" />}
 description="Every project has a plugin Auth key. Visit your console, choose your project, and copy or generate the Auth Key from the settings tab."
          >
            <button
              onClick={() => window.open('/console', '_blank')}
              className="mt-6 px-8 py-4 bg-slate-900 hover:bg-slate-800 border border-slate-700 text-white rounded-xl font-bold flex items-center gap-2 transition-all"
            >
              Open Console <ExternalLink size={20} />
            </button>
          </Step>

          <Step
            number="3"
            title="Install into Studio"
            icon={<FolderInput className="text-blue-400" />}
            description="Open Roblox Studio. Drag the downloaded hollowpad.rbxmx file directly into the viewport to install it instantly."
          />

          <Step
            number="4"
            title="Sign In with Key"
            icon={<LogIn className="text-blue-400" />}
            description="Open the Hollowpad plugin dock in Studio, paste your unique Auth Key, and click 'Sign In'."
          />

          <Step
            number="5"
            title="Prompt & Build"
            icon={<Terminal className="text-blue-400" />}
            description="You are set! Send instructions via the webapp or the plugin to start building your game."
          />

        </div>
      </section>

      {/* --- FOOTER (Exact Replica) --- */}
      <footer className="pt-20 pb-10 border-t border-slate-800 bg-slate-950 text-slate-400 text-sm">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-10 mb-16">
            <div className="col-span-2 md:col-span-1">
                <div className="flex items-center gap-2 mb-4">
                    <img src="/logo512.png" alt="Logo" className="w-6 h-6 rounded-md" />
                    <span className="font-bold text-white text-lg">Hollowpad</span>
                </div>
                <p className="text-slate-500 mb-6">
                    Empowering the next generation of Roblox developers with AI tools.
                </p>
            </div>

            <div>
                <h4 className="font-bold text-white mb-4">Product</h4>
                <ul className="space-y-3">
                    <li><button onClick={() => navigate('/#features')} className="hover:text-blue-400 transition-colors">Features</button></li>
                    <li><button onClick={() => navigate('/#pricing')} className="hover:text-blue-400 transition-colors">Pricing</button></li>
                    <li><button onClick={() => navigate('/#showcase')} className="hover:text-blue-400 transition-colors">Showcase</button></li>
                </ul>
            </div>

            <div>
                <h4 className="font-bold text-white mb-4">Resources</h4>
                <ul className="space-y-3">
                    <li><button onClick={handleDownload} className="hover:text-blue-400 transition-colors">Get Editor plugin</button></li>
                    <li><a href="https://discord.gg/qCYRjK4pd3" className="hover:text-blue-400 transition-colors">Discord Community</a></li>
                </ul>
            </div>

            <div>
                <h4 className="font-bold text-white mb-4">Legal</h4>
                <ul className="space-y-3">
                    <li><button onClick={() => navigate('/terms')} className="hover:text-blue-400 transition-colors text-left">Terms of Service</button></li>
                    <li><button onClick={() => navigate('/privacy')} className="hover:text-blue-400 transition-colors text-left">Privacy Policy</button></li>
                </ul>
            </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 pt-8 border-t border-slate-900 flex flex-col md:flex-row items-center justify-between gap-4">
            <p>&copy; {new Date().getFullYear()} Hollowpad. All rights reserved.</p>
            <p className="text-xs text-slate-600">
                Not affiliated with Roblox Corporation. Roblox is a registered trademark of Roblox Corp.
            </p>
        </div>
      </footer>

    </div>
  );
   };

// --- GUIDE SUB-COMPONENT ---
function Step({ number, title, description, icon, children }) {
  return (
    <div className="flex gap-8 relative group">
      {/* Connector Line */}
      {number !== "5" && (
        <div className="absolute left-[27px] top-14 bottom-[-80px] w-px bg-gradient-to-b from-blue-500/50 via-blue-500/20 to-transparent z-0" />
      )}

      <div className="shrink-0 w-14 h-14 rounded-2xl bg-slate-900 border border-slate-800 flex items-center justify-center text-xl font-black text-white z-10 shadow-2xl transition-all group-hover:border-blue-500/50">
        {number}
      </div>

      <div className="flex-1 pt-2">
        <div className="flex items-center gap-3 mb-3">
          <div className="p-2 bg-blue-500/10 rounded-lg border border-blue-500/20">
            {React.cloneElement(icon, { size: 20 })}
          </div>
          <h3 className="text-2xl font-bold text-white tracking-tight">{title}</h3>
        </div>
        <p className="text-slate-400 text-lg leading-relaxed max-w-2xl">
          {description}
        </p>
        {children}
      </div>
    </div>
  );
};
